// Epic 7 pixel parity (ADO-903) — session-local recent searches for the header
// search typeahead (prototype `ShoplinkSearchModule` "Recent searches" section).
//
// Real user history only (customer-demo posture — never seeded/fake terms):
// a term is recorded whenever a search is COMMITTED (Enter, a suggestion row,
// "View all" or a recent-term row), deduped case-insensitively, most-recent
// first, capped at 5. sessionStorage keeps it session-local (per prototype
// behaviour — recents do not survive the session) and shared across the
// /products remounts an auth-gate re-render can cause.

const RECENT_SEARCHES_KEY = 'shoplink.recentSearches';
const MAX_RECENT_SEARCHES = 5;

/** Read the recent committed search terms, most-recent first (max 5). A missing
 *  or malformed entry is an empty list — never an error. */
export function readRecentSearches(): readonly string[] {
  try {
    const raw = window.sessionStorage.getItem(RECENT_SEARCHES_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((term): term is string => typeof term === 'string' && term.trim() !== '')
      .slice(0, MAX_RECENT_SEARCHES);
  } catch {
    // Malformed JSON / storage unavailable — recents are a nicety, never an error.
    return [];
  }
}

/** Record a committed search term: trimmed, deduped (case-insensitive),
 *  most-recent first, capped at 5. Blank terms are never recorded. */
export function recordRecentSearch(term: string): void {
  const trimmed = term.trim();
  if (trimmed === '') return;
  try {
    const next = [
      trimmed,
      ...readRecentSearches().filter((t) => t.toLowerCase() !== trimmed.toLowerCase()),
    ].slice(0, MAX_RECENT_SEARCHES);
    window.sessionStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    // Storage full / unavailable (private mode) — losing a recent is harmless.
  }
}
